/**
 * Camada vetorial de Mapa Coroplético (Concentração de Indicações por Bairro).
 *
 * Renderiza os 132 bairros e loteamentos oficiais de Artur Nogueira (`/maps/bairros.geojson`)
 * preenchidos em 5 faixas discretas dinâmicas (Âmbar → Laranja → Vermelho → Carmim) mais a
 * faixa neutra (0 indicações), utilizando sempre os dados anteriores ao filtro geográfico
 * de bairro (`cityPins`) para preservar a comparabilidade entre os 132 bairros mesmo quando
 * um bairro específico está selecionado nos painéis laterais.
 *
 * Gerencia um único instância singleton de `L.Tooltip` no mapa para garantir que nunca
 * existam múltiplos tooltips abertos ou empilhados simultaneamente.
 *
 * @module components/ChoroplethLayer
 */

import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GeoJSON, useMap } from "react-leaflet";
import L from "leaflet";
import {
  buildSpatialNeighborhoodIndex,
  computeNeighborhoodChoroplethStats,
  getChoroplethFeatureStyle,
  getChoroplethPalette,
  isNeighborhoodSelected,
} from "../constants/mapConfig";

/** Cache em módulo para evitar múltiplos downloads de `/maps/bairros.geojson` ao alternar camadas */
const geoJsonCache = new Map();

/**
 * Escapa caracteres especiais HTML para construção segura do conteúdo do Tooltip.
 *
 * @param {any} value
 * @returns {string}
 */
function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * Monta o HTML enxuto e legível do Tooltip coroplético para um bairro.
 *
 * @param {any} feature
 * @param {any} stat
 * @param {boolean} isSelected
 * @param {boolean} isDarkMode
 * @returns {string}
 */
export function buildChoroplethTooltipHtml(feature, stat, isSelected, isDarkMode = false) {
  const title = escapeHtml(
    feature?.properties?.fullName || feature?.properties?.name || "Bairro"
  );
  const count = stat?.count ?? 0;
  const percentage = stat?.percentage ?? 0;
  const classIndex = stat?.classIndex ?? 0;
  const dominantCategory = stat?.dominantCategory ?? null;

  const palette = getChoroplethPalette(isDarkMode);
  const swatch = palette[classIndex] || palette[0];

  const countLabel =
    count === 0
      ? "Sem indicações no recorte"
      : count === 1
      ? `1 indicação única (${percentage.toFixed(1).replace(".", ",")}%)`
      : `${count} indicações únicas (${percentage.toFixed(1).replace(".", ",")}%)`;

  const dominantHtml =
    count > 0 && dominantCategory
      ? `<div class="choropleth-tooltip-meta">Tema principal: <strong>${escapeHtml(
          dominantCategory.label
        )}</strong> (${dominantCategory.count})</div>`
      : "";

  const hintHtml = isSelected
    ? `<div class="choropleth-tooltip-hint">Filtro ativo • Clique para limpar</div>`
    : count > 0
    ? `<div class="choropleth-tooltip-hint">Clique para filtrar este bairro</div>`
    : "";

  return `
    <div class="choropleth-tooltip-card">
      <div class="choropleth-tooltip-header">
        <span class="choropleth-tooltip-swatch" style="background:${swatch.fillColor};border-color:${swatch.borderColor};"></span>
        <span class="choropleth-tooltip-title">${title}</span>
      </div>
      <div class="choropleth-tooltip-count">${escapeHtml(countLabel)}</div>
      ${dominantHtml}
      ${hintHtml}
    </div>
  `;
}

/**
 * @param {object} props
 * @param {any[]} [props.cityPins=[]] Pins geolocalizados do recorte global (pré-filtro de bairro)
 * @param {any} [props.selectedNeighborhood=null] Bairro atualmente selecionado
 * @param {(neighborhood: object|null) => void} [props.onSelectNeighborhood] Callback ao clicar em um bairro
 * @param {(meta: any) => void} [props.onChoroplethMetaChange] Callback com metadados das faixas para a legenda
 * @param {boolean} [props.isDarkMode=false] Se o modo escuro está ativo
 * @param {string} [props.url="/maps/bairros.geojson"] URL da malha oficial de bairros
 */
function ChoroplethLayer({
  cityPins = [],
  selectedNeighborhood = null,
  onSelectNeighborhood,
  onChoroplethMetaChange,
  isDarkMode = false,
  url = "/maps/bairros.geojson",
}) {
  const map = useMap();
  const [geoData, setGeoData] = useState(() => geoJsonCache.get(url) || null);

  /** @type {React.MutableRefObject<Map<number, L.Path>>} */
  const layersByIdRef = useRef(new Map());
  /** @type {React.MutableRefObject<Map<number, any>>} */
  const featuresByIdRef = useRef(new Map());
  /** @type {React.MutableRefObject<number|null>} */
  const hoveredFeatureIdRef = useRef(null);
  /** @type {React.MutableRefObject<L.Tooltip|null>} */
  const sharedTooltipRef = useRef(null);

  const callbacksRef = useRef({
    onSelectNeighborhood,
    selectedNeighborhood,
    choroplethStats: null,
    isDarkMode,
  });

  // 1. Carrega `/maps/bairros.geojson` com cache compartilhado em memória
  useEffect(() => {
    if (geoJsonCache.has(url)) {
      setGeoData(geoJsonCache.get(url));
      return undefined;
    }

    let active = true;
    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`Falha HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (data && data.type === "FeatureCollection") {
          geoJsonCache.set(url, data);
          if (active) {
            setGeoData(data);
          }
        }
      })
      .catch((err) => {
        if (import.meta.env.DEV) {
          console.debug("[ChoroplethLayer] Não foi possível carregar bairros.geojson:", err);
        }
      });

    return () => {
      active = false;
    };
  }, [url]);

  // 2. Índice espacial pré-computado uma única vez para as 132 feições
  const spatialIndex = useMemo(
    () => buildSpatialNeighborhoodIndex(geoData?.features || []),
    [geoData]
  );

  // 3. Estatísticas coropléticas calculadas sempre sobre `cityPins` (pré-filtro geográfico de bairro)
  const choroplethStats = useMemo(
    () =>
      computeNeighborhoodChoroplethStats(
        geoData?.features || [],
        cityPins,
        spatialIndex
      ),
    [geoData, cityPins, spatialIndex]
  );

  useEffect(() => {
    callbacksRef.current = {
      onSelectNeighborhood,
      selectedNeighborhood,
      choroplethStats,
      isDarkMode,
    };
  }, [onSelectNeighborhood, selectedNeighborhood, choroplethStats, isDarkMode]);

  // 4. Emite metadados das 5 faixas e contagens para a Legenda e cabeçalho do mapa
  useEffect(() => {
    if (!geoData) return;
    onChoroplethMetaChange?.(choroplethStats);
  }, [geoData, choroplethStats, onChoroplethMetaChange]);

  // Utilitário para fechar o tooltip singleton imediatamente
  const closeSharedTooltip = useCallback(() => {
    const tip = sharedTooltipRef.current;
    if (tip && map && map.hasLayer(tip)) {
      map.removeLayer(tip);
    }
  }, [map]);

  // Utilitário para abrir/posicionar o tooltip singleton no mapa
  const openOrMoveSharedTooltip = useCallback(
    (latlng, html) => {
      if (!map || !latlng) return;
      if (!sharedTooltipRef.current) {
        sharedTooltipRef.current = L.tooltip({
          permanent: false,
          sticky: true,
          direction: "top",
          offset: [0, -10],
          opacity: 1,
          interactive: false,
          className: "choropleth-tooltip",
        });
      }
      const tip = sharedTooltipRef.current;
      tip.setContent(html);
      tip.setLatLng(latlng);
      if (!map.hasLayer(tip)) {
        tip.addTo(map);
      }
    },
    [map]
  );

  // Utilitário para atualizar o estilo de apenas 1 polígono (sem tocar nos outros 131 nem mover nós no DOM)
  const updateSingleLayerStyle = useCallback((featureId, isHovered) => {
    const layer = layersByIdRef.current.get(featureId);
    const feature = featuresByIdRef.current.get(featureId);
    if (!layer || !feature) return;

    const {
      choroplethStats: curStats,
      selectedNeighborhood: curSelected,
      isDarkMode: curDark,
    } = callbacksRef.current;
    const stat = curStats?.statsById?.[featureId];

    layer.setStyle(
      getChoroplethFeatureStyle(feature, {
        count: stat?.count ?? 0,
        breaks: curStats?.breaks || [],
        selectedNeighborhood: curSelected,
        isHovered,
        isDarkMode: curDark,
      })
    );
  }, []);

  // Limpa qualquer tooltip aberto ao arrastar, dar zoom, sair do mapa ou desmontar a camada
  useEffect(() => {
    if (!map) return undefined;

    const handleClearHover = () => {
      const prevId = hoveredFeatureIdRef.current;
      if (prevId !== null) {
        hoveredFeatureIdRef.current = null;
        updateSingleLayerStyle(prevId, false);
      }
      closeSharedTooltip();
    };

    const container = map.getContainer?.();
    map.on("dragstart", handleClearHover);
    map.on("zoomstart", handleClearHover);
    if (container) {
      container.addEventListener("mouseleave", handleClearHover);
    }

    return () => {
      map.off("dragstart", handleClearHover);
      map.off("zoomstart", handleClearHover);
      if (container) {
        container.removeEventListener("mouseleave", handleClearHover);
      }
      closeSharedTooltip();
    };
  }, [map, closeSharedTooltip, updateSingleLayerStyle]);

  // 5. Atualiza estilos dos 132 polígonos quando mudam os dados, o bairro selecionado ou o tema
  useEffect(() => {
    if (!geoData) return;
    const layersMap = layersByIdRef.current;
    let selectedLayer = null;

    for (const feature of geoData.features) {
      const fId = Number(feature.id);
      const layer = layersMap.get(fId);
      if (!layer) continue;

      const stat = choroplethStats.statsById[fId];
      const count = stat?.count ?? 0;
      const isHovered = hoveredFeatureIdRef.current === fId;
      const isSelected = isNeighborhoodSelected(feature, selectedNeighborhood);

      const nextStyle = getChoroplethFeatureStyle(feature, {
        count,
        breaks: choroplethStats.breaks,
        selectedNeighborhood,
        isHovered,
        isDarkMode,
      });

      layer.setStyle(nextStyle);

      if (isSelected) {
        selectedLayer = layer;
      }

      if (
        isHovered &&
        sharedTooltipRef.current &&
        map &&
        map.hasLayer(sharedTooltipRef.current)
      ) {
        sharedTooltipRef.current.setContent(
          buildChoroplethTooltipHtml(feature, stat, isSelected, isDarkMode)
        );
      }
    }

    // Eleva somente o bairro selecionado para garantir que o contorno Azul/Ciano fique íntegro sobre vizinhos
    if (selectedLayer && typeof selectedLayer.bringToFront === "function") {
      selectedLayer.bringToFront();
    }
  }, [geoData, choroplethStats, selectedNeighborhood, isDarkMode, map]);

  if (!geoData) return null;

  return (
    <GeoJSON
      key="neighborhood-choropleth-layer"
      data={geoData}
      style={(feature) => {
        const fId = Number(feature.id);
        const stat = choroplethStats.statsById[fId];
        return getChoroplethFeatureStyle(feature, {
          count: stat?.count ?? 0,
          breaks: choroplethStats.breaks,
          selectedNeighborhood,
          isHovered: hoveredFeatureIdRef.current === fId,
          isDarkMode,
        });
      }}
      onEachFeature={(feature, layer) => {
        const fId = Number(feature.id);
        layersByIdRef.current.set(fId, layer);
        featuresByIdRef.current.set(fId, feature);

        layer.on({
          mouseover: (e) => {
            const prevId = hoveredFeatureIdRef.current;
            if (prevId !== null && prevId !== fId) {
              updateSingleLayerStyle(prevId, false);
            }
            hoveredFeatureIdRef.current = fId;
            updateSingleLayerStyle(fId, true);

            const {
              choroplethStats: curStats,
              selectedNeighborhood: curSelected,
              isDarkMode: curDark,
            } = callbacksRef.current;
            const stat = (curStats || choroplethStats)?.statsById?.[fId];
            const isSelected = isNeighborhoodSelected(feature, curSelected);
            const html = buildChoroplethTooltipHtml(feature, stat, isSelected, curDark);

            openOrMoveSharedTooltip(e.latlng, html);
          },
          mousemove: (e) => {
            if (
              hoveredFeatureIdRef.current === fId &&
              sharedTooltipRef.current &&
              map &&
              map.hasLayer(sharedTooltipRef.current) &&
              e.latlng
            ) {
              sharedTooltipRef.current.setLatLng(e.latlng);
            }
          },
          mouseout: () => {
            updateSingleLayerStyle(fId, false);
            if (hoveredFeatureIdRef.current === fId) {
              hoveredFeatureIdRef.current = null;
              closeSharedTooltip();
            }
          },
          click: (e) => {
            if (e.originalEvent) {
              L.DomEvent.stopPropagation(e.originalEvent);
            }
            const nbInfo = {
              id: feature.id,
              name: feature.properties?.name || "Bairro",
              fullName:
                feature.properties?.fullName || feature.properties?.name || "Bairro",
              center: feature.properties?.center,
            };
            callbacksRef.current.onSelectNeighborhood?.(nbInfo);
          },
        });
      }}
    />
  );
}

export default memo(ChoroplethLayer);
